/* import __COLOCATED_TEMPLATE__ from './workflow-header.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import {
  OPERATOR_CUSTOM_BOT_OVERVIEW_ROUTE,
  WEBHOOK_RECEIVED_TARGET,
} from 'embercom/lib/operator/custom-bots/constants';
import { customBotObjectTypes } from 'embercom/models/data/matching-system/matching-constants';
import {
  OUTBOUND_EDITOR_ROUTES,
  OUTBOUND_INDEX_ROUTES,
  FIN_TASKS_ROUTES,
} from 'embercom/models/outbound/content-wrapper';
import PredicateGroup from 'predicates/models/predicate-group';
import { tracked } from '@glimmer/tracking';
import { isBlank } from '@ember/utils';

export default class WorkflowHeader extends Component {
  @service contentEditorService;
  @service store;
  @service router;
  @service intl;
  @service intercomConfirmService;
  @service intercomEventService;
  @service appService;
  @service guideLibraryService;
  @service finStandaloneService;
  @service workflowLocalizationService;
  @service workflowTitleEditor;
  @service navbarCollapsingService;

  @tracked hasSaved = false;
  @tracked hasDismissedOptInCopy = false;

  maxTitleLength = 255;

  constructor() {
    super(...arguments);
    if (this.triggerPredicates && this.customBot && !this.customBot.triggerPredicateGroup) {
      this.customBot.triggerPredicateGroup = this.store.createFragment(
        'predicates/predicate-group',
        {
          predicates: PredicateGroup.convertRawPredicates(this.store, [
            {
              type: this.triggerPredicates.type,
              predicates: [this.triggerPredicates.options.firstObject.predicate],
            },
          ]),
        },
      );
    }

    //initialize the editor configs for all rulesets
    this.contentEditorService.ruleset.rulesetLinks.forEach((rulesetLink) => {
      if (customBotObjectTypes.includes(rulesetLink.objectType)) {
        // Get active workflow based on feature flag and availability
        let targetWorkflow = rulesetLink.object.activeWorkflow;
        targetWorkflow.editorConfig = rulesetLink.object.config.editorConfig;

        // Encountering an issue where the reply buttons of the live visual builder object was invalid due to a missing editor step config.
        // The reply button validator says blocks-not-empty rule is violated, this is because of the missing step config.
        // To get around this, we initialize the editor config for the live visual builder object as well
        // This is needed to make sure the editor config is configured correctly for the live visual builder object.
        rulesetLink.object.visualBuilderObject.editorConfig =
          rulesetLink.object.config.editorConfig;
      }
    });

    // Initialize the title editor service with current model value
    if (this.isDraftEnabled) {
      this.navbarCollapsingService.collapseNavbar();
      this.workflowTitleEditor.initialize(this.workflow.additionalData.title);
    } else {
      this.workflowTitleEditor.initialize(this.contentEditorService.ruleset.clientData.title);
    }
  }

  get title() {
    return this.workflowTitleEditor.currentTitle;
  }

  @action
  onChangeTitle(value) {
    this.workflowTitleEditor.updateTitle(value);
  }

  @action
  onTitleFocusOut() {
    let finalValue = this.workflowTitleEditor.currentTitle;
    let trimmedValue = finalValue.trim();

    if (this.isDraftEnabled) {
      this.workflow.additionalData.title = trimmedValue;
    } else {
      this.contentEditorService.ruleset.clientData.title = trimmedValue;
    }
  }

  get isInStandaloneSection() {
    return this.router.currentRouteName.includes('apps.app.standalone');
  }

  get triggerPredicates() {
    if (!this.customBot?.config) {
      return;
    }

    return this.customBot.config.triggerPredicates;
  }

  get customBot() {
    return this.contentEditorService.activeObject;
  }

  get workflow() {
    let { editorConfig } = this.customBot.config;
    let workflow = this.customBot.activeWorkflow;
    workflow.editorConfig = editorConfig;
    return workflow;
  }

  get editorConfiguration() {
    let { editorConfig } = this.customBot.config;
    if (!editorConfig) {
      throw new Error(
        `Cannot resolve path configuration class for custom bot type=${this.customBot.type}`,
      );
    }
    return editorConfig;
  }

  get isBackgroundWorkflow() {
    return this.customBot.activeWorkflow?.isCustomerFacing === false;
  }

  get shouldShowOptInCopy() {
    return (
      !this.hasDismissedOptInCopy &&
      (this.customBot.shouldShowOptInCopyForAiAnswers ||
        this.customBot.shouldShowOptInCopyForAiCategorization ||
        this.customBot.shouldShowOptInCopyForAiAutoTranslation)
    );
  }

  get automaticOptInMessage() {
    if (this.customBot.shouldShowOptInCopyForAiCategorization) {
      return this.intl.t(
        'operator.workflows.visual-builder.classify-conversation-automatic-ai-opt-in',
        {
          htmlSafe: true,
        },
      );
    } else if (this.customBot.shouldShowOptInCopyForAiAutoTranslation) {
      return this.intl.t('operator.workflows.visual-builder.auto-translate-automatic-ai-opt-in', {
        htmlSafe: true,
      });
    }

    return this.intl.t('ai-agent.profiles.answers.ai.opt-in', {
      finPricingLearnMoreUrl: this.appService.app.finPricingLearnMoreUrl,
      htmlSafe: true,
    });
  }

  get validLocalVariableIdentifiersUsedInWorkflow() {
    return this.contentEditorService.activeObject.validLocalVariableIdentifiersUsedInWorkflow;
  }

  get setLiveErrors() {
    if (this.customBot.isAiAgentWorkflowTrigger) {
      return this._buildFinTaskValidationTooltipContent();
    }
  }

  get isInStandaloneTasksSection() {
    return this.router.currentRouteName.includes('apps.app.standalone.tasks.workflow');
  }

  get shouldDisablePauseButton() {
    return this.contentEditorService.activeObject?.canUpdateState === false;
  }

  get isInStandaloneWorkflowsSection() {
    return this.isInStandaloneSection && !this.isInStandaloneTasksSection;
  }

  get isDraftEnabled() {
    return this.customBot.canUseDraftWorkflow();
  }
  get shouldShowSaveAsDraftButton() {
    return (
      this.isDraftEnabled &&
      this.customBot.isAiAgentWorkflowTrigger &&
      this.contentEditorService.ruleset.isLive
    );
  }

  get hasDraftWorkflowDifferentFromLive() {
    if (!this.isDraftEnabled) {
      return false;
    }

    if (!this.customBot.draftVisualBuilderObject || !this.customBot.visualBuilderObject) {
      return false;
    }
    return this.customBot.draftVisualBuilderObject.id !== this.customBot.visualBuilderObject.id;
  }

  get draftHasUnsavedChanges() {
    return (
      this.customBot.draftVisualBuilderObject?.hasUnsavedChanges ||
      this.workflowTitleEditor.hasChanged ||
      false
    );
  }

  get disableSaveAsDraftButton() {
    return (
      this.saveAsDraft.isRunning ||
      this.contentEditorService.ruleset.isSaving ||
      !this.draftHasUnsavedChanges ||
      this.customBot.draftVisualBuilderObject.validations.isInvalid
    );
  }

  get hasActiveInstagramIntegration() {
    return this.store.peekAll('instagram/integration').length > 0;
  }

  get hasActiveFacebookIntegration() {
    // Note: this is just checking the appstore package and not the individual facebook pages since they require further identification
    return this.store.peekRecord('appstore/app-package', 'facebook')?.isInstalled || false;
  }

  get hasActivatedSms() {
    return this.store.peekAll('sms/phone-number').length > 0;
  }

  get invalidSetupForChannels() {
    if (!this.customBot?.targetChannels) {
      return [];
    }

    let targetChannels = this.customBot.targetChannels;
    let invalidSetupForChannels = [];

    if (
      new Set(targetChannels).intersection(new Set(['web', 'ios', 'android'])).size > 0 &&
      !this.guideLibraryService.isMessengerInstalled
    ) {
      invalidSetupForChannels.push('messenger');
    }
    if (targetChannels.includes('whatsapp') && !this.appService.app?.hasActiveWhatsAppIntegration) {
      invalidSetupForChannels.push('whatsapp');
    }
    if (targetChannels.includes('instagram') && !this.hasActiveInstagramIntegration) {
      invalidSetupForChannels.push('instagram');
    }
    if (targetChannels.includes('facebook') && !this.hasActiveFacebookIntegration) {
      invalidSetupForChannels.push('facebook');
    }
    if (targetChannels.includes('slack') && !this.appService.app?.hasSlackIntegration) {
      invalidSetupForChannels.push('slack');
    }
    if (targetChannels.includes('sms') && !this.hasActivatedSms) {
      invalidSetupForChannels.push('sms');
    }

    return invalidSetupForChannels;
  }

  get shouldShowInvalidSetupBanner() {
    if (!this.appService.app.canUseFeature('team-ai-agent-new-workflow-channel-setup-banner')) {
      return false;
    }

    // exclude SA apps
    if (this.appService.app?.canUseStandalone) {
      return false;
    }

    // exclude reusable workflows
    if (this.customBot.isTriggerable && this.customBot.isNoTrigger) {
      return false;
    }

    // exclude phone call workflows
    if (this.customBot.isPhoneCallTrigger) {
      return false;
    }

    // exclude Fin Agent API workflows
    if (this.customBot.isFinAgentApiConversationTrigger) {
      return false;
    }

    // exclude webhook triggers (Jira, etc.)
    if (this.customBot.target === WEBHOOK_RECEIVED_TARGET) {
      return false;
    }

    return this.invalidSetupForChannels.length > 0;
  }

  get bannerButtonLabel() {
    if (this.invalidSetupForChannels.length === 1) {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.install',
      );
    } else {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.manage-channels',
      );
    }
  }

  get bannerDescriptionLabel() {
    if (this.invalidSetupForChannels.length === 1) {
      return this.translationForChannelBanner(this.invalidSetupForChannels[0]);
    } else {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.multiple-channels',
        {
          channels: this.invalidSetupForChannels
            .slice(0, this.invalidSetupForChannels.length - 1)
            .map((channel) => {
              return this.translationForChannelName(channel);
            })
            .join(', '),
          lastChannel: this.translationForChannelName(
            this.invalidSetupForChannels[this.invalidSetupForChannels.length - 1],
          ),
        },
      );
    }
  }

  translationForChannelBanner(channel) {
    if (channel === 'messenger') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.messenger',
      );
    }
    if (channel === 'whatsapp') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.whatsapp',
      );
    }
    if (channel === 'facebook') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.facebook',
      );
    }
    if (channel === 'instagram') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.instagram',
      );
    }
    if (channel === 'slack') {
      return this.intl.t('content-editor.headers.edit-mode.standard-header.banner.workflow.slack');
    }
    if (channel === 'sms') {
      return this.intl.t('content-editor.headers.edit-mode.standard-header.banner.workflow.sms');
    }

    return null;
  }

  translationForChannelName(channel) {
    if (channel === 'messenger') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.channels.messenger',
      );
    }
    if (channel === 'whatsapp') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.channels.whatsapp',
      );
    }
    if (channel === 'facebook') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.channels.facebook',
      );
    }
    if (channel === 'instagram') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.channels.instagram',
      );
    }
    if (channel === 'slack') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.channels.slack',
      );
    }
    if (channel === 'sms') {
      return this.intl.t(
        'content-editor.headers.edit-mode.standard-header.banner.workflow.channels.sms',
      );
    }

    return null;
  }

  @action
  transitionToInstallationPage() {
    if (this.invalidSetupForChannels.length > 1) {
      return this.router.transitionTo('apps.app.settings.channels.all');
    }
    let channel = this.invalidSetupForChannels[0];

    if (channel === 'facebook') {
      return this.router.transitionTo('apps.app.settings.channels.social-channels', {
        queryParams: { tab: 'facebook' },
      });
    } else if (channel === 'instagram') {
      return this.router.transitionTo('apps.app.settings.channels.social-channels', {
        queryParams: { tab: 'instagram' },
      });
    } else {
      return this.router.transitionTo(`apps.app.settings.channels.${channel}`);
    }
  }

  @action
  async save() {
    if (
      this.args.config.preActivationCheck &&
      (this.contentEditorService.ruleset.isLive || this.contentEditorService.ruleset.isScheduled)
    ) {
      this.args.config.preActivationCheck(this.saveRuleset);
      return;
    }
    this.saveRuleset.perform();
    this.hasSaved = true;
  }

  get isDraft() {
    let workflow = this.workflow;

    if (!this.customBot || !workflow) {
      return false;
    }

    // If there's a draft workflow and the current workflow matches the draft workflow ID,
    // then we're viewing the draft
    return !!(
      this.customBot.draftVisualBuilderObject &&
      workflow.id === this.customBot.draftVisualBuilderObject.id
    );
  }

  @restartableTask
  *saveRuleset() {
    yield this.contentEditorService.saveRuleset.perform();
    if (this.args.config.postActivationCheck) {
      this.args.config.postActivationCheck();
    }
    this.workflowTitleEditor.resetHasChanged();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'workflow_set_changes_live',
      place: 'workflow_editor',
      section: 'header',
      workflow_state: this.contentEditorService.ruleset.state,
      has_draft_different_from_live: this.hasDraftWorkflowDifferentFromLive,
      is_standalone: this.isInStandaloneSection,
    });
  }

  @restartableTask
  *saveAsDraft() {
    // Skip preActivationCheck entirely - no confirmation dialog for draft saves
    yield this.contentEditorService.saveAsDraft.perform();
    this.hasSaved = true;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'workflow_save_as_draft',
      place: 'workflow_editor',
      section: 'header',
      workflow_state: this.contentEditorService.ruleset.state,
      has_draft_different_from_live: this.hasDraftWorkflowDifferentFromLive,
      is_standalone: this.isInStandaloneSection,
    });
  }

  @restartableTask
  *discardDraft() {
    yield this.contentEditorService.discardDraft.perform();
    this.hasSaved = true;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'workflow_revert_to_live_or_paused',
      place: 'workflow_editor',
      section: 'header',
      workflow_state: this.contentEditorService.ruleset.state,
      has_draft_different_from_live: this.hasDraftWorkflowDifferentFromLive,
      is_standalone: this.isInStandaloneSection,
    });
  }

  @action
  async close() {
    this.contentEditorService.uploadFileFinished();

    if (this.contentEditorService.ruleset.hasUnsavedChanges) {
      let confirmed = await this.confirmUnsavedChanges();
      if (!confirmed) {
        return;
      }

      this.contentEditorService.ruleset.rollbackAttributes();
    }

    if (this.contentEditorService.returnPath) {
      this.router.transitionTo(this.contentEditorService.returnPath);
    } else if (this.isInStandaloneSection) {
      this.router.transitionTo(this.finStandaloneService.workflowsReturnRoute);
    } else if (this.router.currentRouteName === FIN_TASKS_ROUTES.task) {
      this.router.transitionTo(FIN_TASKS_ROUTES.tasks);
    } else {
      this.contentEditorService.changeToViewMode();
      this.router.currentRouteName === OUTBOUND_EDITOR_ROUTES['custom_bot']
        ? this.router.transitionTo(OUTBOUND_INDEX_ROUTES['custom_bot'])
        : this.router.transitionTo(OPERATOR_CUSTOM_BOT_OVERVIEW_ROUTE);
    }
  }

  confirmUnsavedChanges() {
    return this.intercomConfirmService.confirm({
      title: this.intl.t('content-editor.headers.edit-mode.standard-header.unsaved-changes-title'),
      body: this.intl.t('content-editor.headers.edit-mode.standard-header.unsaved-changes-body'),
      confirmButtonText: this.intl.t(
        'content-editor.headers.edit-mode.standard-header.unsaved-changes-confirm',
      ),
      primaryButtonType: 'primary-destructive',
      cancelButtonText: this.intl.t(
        'content-editor.headers.edit-mode.standard-header.unsaved-changes-cancel',
      ),
    });
  }

  _buildFinTaskValidationTooltipContent() {
    let missingFields = [];

    if (this.contentEditorService.ruleset.clientData.validations.attrs.title.isInvalid) {
      missingFields.push(
        this.contentEditorService.ruleset.clientData.validations.attrs.title.message,
      );
    }

    if (this.isDraftEnabled) {
      if (isBlank(this.workflow.additionalData.description)) {
        missingFields.push(
          this.intl.t(
            'operator.workflows.visual-builder.workflow-trigger-node.fin-task.missing-description',
          ),
        );
      }
    } else if (isBlank(this.customBot.description)) {
      missingFields.push(
        this.intl.t(
          'operator.workflows.visual-builder.workflow-trigger-node.fin-task.missing-description',
        ),
      );
    }

    let invalidSteps = this.customBot.visualBuilderObject.groups.toArray().flatMap((g) => {
      return g.steps.filter(
        (s) => s.type === 'operator/visual-builder/step/fin-custom' && s.validations.isInvalid,
      );
    });

    if (
      invalidSteps.toArray().some((step) => step.validations.attrs.finProcedureContent.isInvalid)
    ) {
      missingFields.push(
        this.intl.t(
          'operator.workflows.visual-builder.workflow-trigger-node.fin-task.missing-instructions',
        ),
      );
    }

    if (invalidSteps.toArray().some((step) => step.validations.attrs.blocks.isInvalid)) {
      missingFields.push(
        this.intl.t(
          'operator.workflows.visual-builder.workflow-trigger-node.fin-task.missing-goal',
        ),
      );
    }

    if (missingFields.length === 0) {
      return null;
    }

    return this.intl
      .t(
        'operator.workflows.visual-builder.workflow-trigger-node.fin-task.set-live-missing-details-error',
      )
      .concat('<ul>')
      .concat(missingFields.map((field) => `<li>${field}</li>`).join(''))
      .concat('</ul>');
  }

  @action
  async exitEditMode() {
    this.contentEditorService.uploadFileFinished();

    if (this.contentEditorService.ruleset.hasUnsavedChanges) {
      let confirmed = await this.confirmUnsavedChanges();
      if (!confirmed) {
        return;
      }

      this.contentEditorService.ruleset.rollbackAttributes();
    }

    this.navbarCollapsingService.expandNavbar();
    this.contentEditorService.changeToViewMode();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'workflow_exit_edit_mode',
      place: 'workflow_editor',
      section: 'header',
      workflow_state: this.contentEditorService.ruleset.state,
      has_draft_different_from_live: this.hasDraftWorkflowDifferentFromLive,
      has_unsaved_changes: this.contentEditorService.ruleset.hasUnsavedChanges,
      is_standalone: this.isInStandaloneSection,
    });
  }

  get lastUpdatedAt() {
    return this.workflow.updatedAt || this.contentEditorService.activeObject.lastUpdatedAt;
  }

  get lastUpdatedBy() {
    return this.workflow.createdBy || this.contentEditorService.activeObject.lastUpdatedBy;
  }
}
